import type {
  AgentWorkspaceAnchor,
  AgentWorkspaceRequirement,
} from "@/features/agent-workspace";
import {
  COGO_COLLAB_LEDGER_SCHEMA_VERSION,
  COGO_COLLAB_SCHEMA_VERSION,
  COGO_USER_ACTOR,
  type CogoCollabConflict,
  type CogoCollabActor,
  type CogoCollabLedger,
  type CogoCollabTransaction,
  type CogoCollabTransactionStatus,
  type CogoJsonPatchOp,
  type CogoPreviewAnchor,
} from "./types";
import {
  detectCogoCollabConflicts,
  markTransactionConflicted,
} from "./conflict";
import { createCogoHistoryState, pushCogoHistoryEvent } from "./history";
import { createPreviewAnchorFromAgentWorkspaceAnchor } from "./preview-anchor";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function metadataOf(project: unknown): Record<string, unknown> {
  if (!isRecord(project)) return {};
  return isRecord(project.metadata) ? project.metadata : {};
}

function readArray<T>(
  value: unknown,
  predicate: (entry: unknown) => entry is T,
): T[] {
  return Array.isArray(value) ? value.filter(predicate) : [];
}

function isTransaction(value: unknown): value is CogoCollabTransaction {
  return isRecord(value) && value.schemaVersion === COGO_COLLAB_SCHEMA_VERSION;
}

function isLedger(value: unknown): value is CogoCollabLedger {
  return (
    isRecord(value) && value.schemaVersion === COGO_COLLAB_LEDGER_SCHEMA_VERSION
  );
}

function uniqueById<T extends { id: string }>(items: T[]): T[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}

function requirementIdFromEffect(
  effect: CogoCollabTransaction["effects"][number],
): string | null {
  if (effect.type !== "user-comment" || !isRecord(effect.payload)) return null;
  const requirementId = effect.payload.requirementId;
  return typeof requirementId === "string" ? requirementId : null;
}

function transactionTouchesRequirement(
  transaction: CogoCollabTransaction,
  requirementId: string,
): boolean {
  const requirementPath = `/metadata/agentWorkspaceRequirements/${requirementId}`;
  return (
    transaction.effects.some(
      (effect) => requirementIdFromEffect(effect) === requirementId,
    ) ||
    transaction.changes.some(
      (change) =>
        change.path === requirementPath || change.from === requirementPath,
    )
  );
}

export function createCogoCollabLedger(updatedAt: string): CogoCollabLedger {
  return {
    schemaVersion: COGO_COLLAB_LEDGER_SCHEMA_VERSION,
    syncedVersion: 0,
    localVersion: 0,
    unconfirmedTransactionIds: [],
    transactions: [],
    conflicts: [],
    history: createCogoHistoryState(),
    updatedAt,
  };
}

export function readCogoCollabLedger(project: unknown): CogoCollabLedger {
  const metadata = metadataOf(project);
  if (isLedger(metadata.cogoCollaboration)) {
    const ledger = metadata.cogoCollaboration;
    const localVersion = ledger.transactions.reduce(
      (version, transaction) => Math.max(version, transaction.versionAfter),
      ledger.localVersion ?? ledger.syncedVersion,
    );
    return {
      ...ledger,
      localVersion,
      unconfirmedTransactionIds: Array.isArray(ledger.unconfirmedTransactionIds)
        ? ledger.unconfirmedTransactionIds
        : ledger.transactions.map((transaction) => transaction.id),
      history: ledger.history ?? createCogoHistoryState(),
    };
  }
  const updatedAt = new Date(0).toISOString();
  const transactions = readArray(
    metadata.cogoCollaborationTransactions,
    isTransaction,
  );
  const localVersion = transactions.reduce(
    (version, transaction) => Math.max(version, transaction.versionAfter),
    0,
  );
  return {
    ...createCogoCollabLedger(updatedAt),
    localVersion,
    unconfirmedTransactionIds: transactions.map(
      (transaction) => transaction.id,
    ),
    transactions,
  };
}

export function createCogoCollabTransaction(input: {
  id: string;
  versionBefore: number;
  actor?: CogoCollabActor;
  target: CogoPreviewAnchor;
  changes: CogoJsonPatchOp[];
  effects?: CogoCollabTransaction["effects"];
  status?: CogoCollabTransactionStatus;
  createdAt?: string;
}): CogoCollabTransaction {
  const createdAt = input.createdAt ?? new Date().toISOString();
  return {
    schemaVersion: COGO_COLLAB_SCHEMA_VERSION,
    id: input.id,
    versionBefore: input.versionBefore,
    versionAfter: input.versionBefore + 1,
    actor: input.actor ?? COGO_USER_ACTOR,
    target: input.target,
    changes: input.changes,
    effects: input.effects ?? [],
    status: input.status ?? "proposed",
    createdAt,
  };
}

export function createCogoCollabTransactionFromRequirement(input: {
  transactionId: string;
  versionBefore: number;
  anchor: AgentWorkspaceAnchor;
  requirement: AgentWorkspaceRequirement;
  appId?: string;
  createdAt?: string;
}): CogoCollabTransaction {
  const target = createPreviewAnchorFromAgentWorkspaceAnchor({
    anchor: input.anchor,
    appId: input.appId,
  });
  return createCogoCollabTransaction({
    id: input.transactionId,
    versionBefore: input.versionBefore,
    target,
    createdAt: input.createdAt ?? input.requirement.createdAt,
    changes: [
      {
        op: "add",
        path: `/metadata/agentWorkspaceRequirements/${input.requirement.requirementId}`,
        value: input.requirement,
      },
    ],
    effects: [
      { type: "preview-anchor", payload: target },
      {
        type: "user-comment",
        payload: {
          requirementId: input.requirement.requirementId,
          body: input.requirement.body,
        },
      },
      {
        type: "agent-rationale",
        payload: {
          body: "Record as a proposal first; accepted changes must flow through the deterministic Coogo apply pipeline.",
        },
      },
    ],
  });
}

export function transitionCogoCollabTransaction(
  transaction: CogoCollabTransaction,
  status: CogoCollabTransactionStatus,
  updatedAt = new Date().toISOString(),
): CogoCollabTransaction {
  return { ...transaction, status, updatedAt };
}

export function findCogoCollabTransactionForRequirement(
  project: unknown,
  requirementId: string,
): CogoCollabTransaction | null {
  return (
    readCogoCollabLedger(project)
      .transactions.filter((transaction) =>
        transactionTouchesRequirement(transaction, requirementId),
      )
      .at(-1) ?? null
  );
}

export function findCogoCollabConflictsForTransaction(
  project: unknown,
  transactionId: string,
): CogoCollabConflict[] {
  return readCogoCollabLedger(project).conflicts.filter(
    (conflict) =>
      conflict.localId === transactionId ||
      conflict.incomingId === transactionId,
  );
}

export function transitionCogoCollabTransactionInProject(input: {
  project: unknown;
  transactionId: string;
  status: CogoCollabTransactionStatus;
  userEvent?: string;
  updatedAt?: string;
}): Record<string, unknown> {
  const projectRecord = isRecord(input.project) ? input.project : {};
  const metadata = metadataOf(projectRecord);
  const ledger = readCogoCollabLedger(projectRecord);
  const updatedAt = input.updatedAt ?? new Date().toISOString();
  const transaction = ledger.transactions.find(
    (entry) => entry.id === input.transactionId,
  );
  if (!transaction) return projectRecord;

  const nextTransaction = transitionCogoCollabTransaction(
    transaction,
    input.status,
    updatedAt,
  );
  const nextTransactions = ledger.transactions.map((entry) =>
    entry.id === input.transactionId ? nextTransaction : entry,
  );
  const isFinalStatus =
    input.status === "applied" || input.status === "rejected";
  const history = pushCogoHistoryEvent({
    state: ledger.history,
    transaction: nextTransaction,
    userEvent: input.userEvent ?? `proposal.${input.status}`,
  });

  return {
    ...projectRecord,
    metadata: {
      ...metadata,
      cogoCollaboration: {
        ...ledger,
        transactions: nextTransactions,
        unconfirmedTransactionIds: isFinalStatus
          ? ledger.unconfirmedTransactionIds.filter(
              (id) => id !== input.transactionId,
            )
          : ledger.unconfirmedTransactionIds,
        history,
        updatedAt,
      } satisfies CogoCollabLedger,
    },
  };
}

export function summarizeCogoCollabRequirement(
  project: unknown,
  requirementId: string,
): {
  transaction: CogoCollabTransaction | null;
  conflicts: CogoCollabConflict[];
  message: string | null;
} {
  const transaction = findCogoCollabTransactionForRequirement(
    project,
    requirementId,
  );
  if (!transaction) {
    return { transaction: null, conflicts: [], message: null };
  }
  const conflicts = findCogoCollabConflictsForTransaction(
    project,
    transaction.id,
  );
  if (transaction.status === "conflicted" || conflicts.length > 0) {
    return {
      transaction,
      conflicts,
      message: "Review needed.",
    };
  }
  if (transaction.status === "applied") {
    return {
      transaction,
      conflicts,
      message: "Applied.",
    };
  }
  if (transaction.status === "rejected") {
    return {
      transaction,
      conflicts,
      message: "Set aside.",
    };
  }
  return {
    transaction,
    conflicts,
    message: "Ready.",
  };
}

export function appendCogoCollabTransactionToProject(input: {
  project: unknown;
  transaction: CogoCollabTransaction;
  userEvent?: string;
  updatedAt?: string;
}): Record<string, unknown> {
  const projectRecord = isRecord(input.project) ? input.project : {};
  const metadata = metadataOf(projectRecord);
  const ledger = readCogoCollabLedger(projectRecord);
  const updatedAt = input.updatedAt ?? input.transaction.createdAt;
  const versionBefore = Math.max(
    input.transaction.versionBefore,
    ledger.localVersion,
  );
  const versionedTransaction =
    versionBefore === input.transaction.versionBefore
      ? input.transaction
      : {
          ...input.transaction,
          versionBefore,
          versionAfter: versionBefore + 1,
        };
  const conflicts = detectCogoCollabConflicts({
    existing: ledger.transactions,
    incoming: versionedTransaction,
    createdAt: updatedAt,
  });
  const transaction = markTransactionConflicted(
    versionedTransaction,
    conflicts,
  );
  const nextTransactions = uniqueById([...ledger.transactions, transaction]);
  const nextConflicts = uniqueById([...ledger.conflicts, ...conflicts]);
  const history = pushCogoHistoryEvent({
    state: ledger.history,
    transaction,
    userEvent: input.userEvent ?? "proposal.create",
  });
  const nextLedger: CogoCollabLedger = {
    ...ledger,
    localVersion: Math.max(ledger.localVersion, transaction.versionAfter),
    unconfirmedTransactionIds: uniqueById([
      ...ledger.unconfirmedTransactionIds.map((id) => ({ id })),
      { id: transaction.id },
    ]).map((entry) => entry.id),
    transactions: nextTransactions,
    conflicts: nextConflicts,
    history,
    updatedAt,
  };
  return {
    ...projectRecord,
    metadata: {
      ...metadata,
      cogoCollaboration: nextLedger,
    },
  };
}
